import * as React from 'react';
import type { TranslationObject } from '@web-config-app/core';
import { Box, Button, StackLayout } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { AiAssistCollapsible } from '@web-config-app/core-react-ui';
import { JsonLogicInputTopBarDataPointButton } from './json-logic-input-top-bar-data-point-button.component';

interface JsonLogicTopBarAiAssistConfig {
  onGenerate: (prompt: string) => Promise<void>;
  assumptionsText?: string;
  promptPlaceholder?: TranslationObject;
  prompt?: string;
  onPromptChange?: (prompt: string) => void;
}

export const JsonLogicInputTopBar = ({
  showDataPoints,
  aiAssist,
}: {
  showDataPoints: boolean;
  aiAssist?: JsonLogicTopBarAiAssistConfig;
}) => {
  const { formatMessage } = useIntl();
  const [isAiAssistOpen, setIsAiAssistOpen] = React.useState(false);
  const canShowAiAssist = !!aiAssist;
  const toggleButtonRef = React.useRef<HTMLButtonElement>(null);
  const prevOpenRef = React.useRef(isAiAssistOpen);
  const topBarActions = (
    <StackLayout
      orientation="horizontal"
      verticalAlignment="center"
      horizontalAlignment="end"
      css={{ flex: '1 1 auto', minWidth: 0, flexWrap: 'nowrap' }}
    >
      {showDataPoints && <JsonLogicInputTopBarDataPointButton />}
      <Button
        as="a"
        icon="interfaceQuestionCircle"
        quiet
        size="small"
        priority="tertiary"
        target="_blank"
        href="https://everlong.atlassian.net/wiki/spaces/CACT/pages/5221974035/Rule+Configuration"
        css={{ whiteSpace: 'nowrap', margin: '$none' }}
      >
        {formatMessage({ id: 'HELP' })}
      </Button>
    </StackLayout>
  );

  React.useEffect(() => {
    if (prevOpenRef.current && !isAiAssistOpen && toggleButtonRef.current) {
      toggleButtonRef.current.focus();
    }
    prevOpenRef.current = isAiAssistOpen;
  }, [isAiAssistOpen]);

  return (
    <StackLayout css={{ width: '100%' }} spacing="$quarter">
      {canShowAiAssist ? (
        <AiAssistCollapsible
          isOpen={isAiAssistOpen}
          onOpenChange={setIsAiAssistOpen}
          toggleButtonRef={toggleButtonRef}
          onGenerate={aiAssist.onGenerate}
          assumptionsText={aiAssist.assumptionsText}
          promptPlaceholder={aiAssist.promptPlaceholder}
          prompt={aiAssist.prompt}
          onPromptChange={aiAssist.onPromptChange}
          generateLabelKey="GENERATE_JSON_LOGIC"
          generatingLabelKey="AI_ASSIST_GENERATING"
          errorMessageKey="AI_GENERATION_ERROR"
          promptInputLabelKey="AI_PROMPT_INPUT_LABEL"
          assumptionsLabelKey="AI_ASSUMPTIONS_LABEL"
          trailingContent={topBarActions}
        />
      ) : (
        <StackLayout
          orientation="horizontal"
          verticalAlignment="center"
          css={{ width: '100%', justifyContent: 'space-between' }}
        >
          <Box aria-hidden css={{ flex: '0 1 auto', minWidth: 0 }} />
          {topBarActions}
        </StackLayout>
      )}
    </StackLayout>
  );
};
